import { changesGenericViewMetadata, forgetGenericViewMetadata } from './genericViewMetadataReads';
import { realtimeSubscribe, realtimeSubscribeFor } from './realtimeSocket';
import { WORK_ITEM_EVENT_FIELDS } from './workItemFields';
import type {
  IMyAttentionChange,
  IMyViewChoicesChange,
  IMyWorkAreasChange,
  IWorkAreaDataChange,
  IWorkItemEvent,
} from './WorkItemApi';

/**
 * T-0770 (`docs/concept-mobile-app.md`, LC-6b): the realtime streams a reader is subscribed to, in a
 * module of their own. The launch subscribes to the person's attention and work areas and to each
 * work area's doorbell, and none of that needs the rest of the work-item API; `WorkItemApi` offers
 * the same functions as its own methods, so every caller that holds the API is unchanged.
 */

/**
 * `afterRevision: null` subscribes to live events only — the right choice for a caller that
 * re-queries on any change and ignores the payload. A number replays the work area's history
 * from that revision first, which only a caller applying deltas incrementally wants.
 */
export function subscribeToEvents(
  projectId: string,
  afterRevision: number | null,
  onEvent: (event: IWorkItemEvent) => void,
  onError?: (error: unknown) => void,
): () => void {
  const disposeSubscription = realtimeSubscribeFor<{
    workItemEvents: IWorkItemEvent;
  }>(
    projectId,
    {
      query: `subscription WorkItemEvents($workAreaId: ID!, $afterRevision: Float) {
      workItemEvents(workAreaId: $workAreaId, afterRevision: $afterRevision) { ${WORK_ITEM_EVENT_FIELDS} }
    }`,
      variables: { workAreaId: projectId, afterRevision },
    },
    {
      next: ({ data, errors }) => {
        if (data?.workItemEvents) {
          onEvent(data.workItemEvents);
          return;
        }
        // A `next` frame carrying `errors` never reached `error`, so a stream that delivered
        // nothing but field errors looked exactly like a quiet one. That is how a wholly dead
        // realtime leg survived: every view silently fell back to its interval poll.
        onError?.(
          new Error(
            errors?.length
              ? `Live updates rejected by the server: ${errors.map((entry) => entry.message).join('; ')}`
              : 'Live updates delivered a frame with no event.',
          ),
        );
      },
      error: (error) => {
        onError?.(error);
      },
      complete: () => undefined,
    },
  );

  // The client is shared, so it is not disposed here: it is lazy and closes itself once the
  // last subscription in the tab goes away.
  return disposeSubscription;
}

/**
 * The other half of the doorbell: everything that changes a view without producing a work-item
 * event — a comment, a field-history row, a commit link, a workflow-state rename, a release.
 * Those reach the database through triggers and other services, so the server emits this one
 * from the database itself. It carries no payload worth applying, only the fact that the work
 * area moved; the caller re-reads its own checkpoint.
 *
 * Multiplexes over the same socket as `subscribeToEvents`.
 */
/** The signed-in person's own attention stream, across every work area (F-0236). */
export function subscribeToMyAttention(
  onChange: (change: IMyAttentionChange) => void,
  onError?: (error: unknown) => void,
): () => void {
  return realtimeSubscribe<{ myAttentionChanges: IMyAttentionChange }>(
    {
      query: `subscription MyAttentionChanges {
      myAttentionChanges { reason at workAreaId itemKey unreadCount }
    }`,
    },
    {
      next: ({ data, errors }) => {
        if (data?.myAttentionChanges) {
          onChange(data.myAttentionChanges);
          return;
        }
        onError?.(
          new Error(
            errors?.length
              ? `Attention updates rejected by the server: ${errors.map((entry) => entry.message).join('; ')}`
              : 'Attention updates delivered a frame with no change.',
          ),
        );
      },
      error: (error) => onError?.(error),
      // A stream that ends (the socket was disposed on sign-out) is reopened by its follower.
      complete: () => onError?.(new Error('Attention updates ended.')),
    },
  );
}

/**
 * The work areas the signed-in person sees changed (I-1525, AC-19.19): re-read them. The frame
 * names no work area; the server sends it only when this person's own listing changed.
 */
export function subscribeToMyWorkAreas(
  onChange: (change: IMyWorkAreasChange) => void,
  onError?: (error: unknown) => void,
): () => void {
  return realtimeSubscribe<{ myWorkAreasChanges: IMyWorkAreasChange }>(
    {
      query: `subscription MyWorkAreasChanges {
      myWorkAreasChanges { at }
    }`,
    },
    {
      next: ({ data, errors }) => {
        if (data?.myWorkAreasChanges) {
          onChange(data.myWorkAreasChanges);
          return;
        }
        onError?.(
          new Error(
            errors?.length
              ? `Work-area updates rejected by the server: ${errors.map((entry) => entry.message).join('; ')}`
              : 'Work-area updates delivered a frame with no change.',
          ),
        );
      },
      error: (error) => onError?.(error),
      complete: () => onError?.(new Error('Work-area updates ended.')),
    },
  );
}

/**
 * The signed-in person's saved choices in one of their views changed on another device (F-0531,
 * `docs/concept-generic-view-engine.md` V13): read that view's choices again. The frame names the
 * view, never a choice, and only the person's own devices are sent it.
 */
export function subscribeToMyViewChoices(
  onChange: (change: IMyViewChoicesChange) => void,
  onError?: (error: unknown) => void,
): () => void {
  return realtimeSubscribe<{ myViewChoicesChanges: IMyViewChoicesChange }>(
    {
      query: `subscription MyViewChoicesChanges {
      myViewChoicesChanges { scopeKind scopeId viewId revision at }
    }`,
    },
    {
      next: ({ data, errors }) => {
        if (data?.myViewChoicesChanges) {
          onChange(data.myViewChoicesChanges);
          return;
        }
        onError?.(
          new Error(
            errors?.length
              ? `View-choice updates rejected by the server: ${errors.map((entry) => entry.message).join('; ')}`
              : 'View-choice updates delivered a frame with no change.',
          ),
        );
      },
      error: (error) => onError?.(error),
      complete: () => onError?.(new Error('View-choice updates ended.')),
    },
  );
}

/**
 * T-0607 IT-21: the doorbell of many work areas on the main connection, on ONE stream — what a
 * list across work areas needs, in place of `subscribeToDataChanges` once for each (and
 * `subscribeToEvents` besides: 400 streams at 200 work areas). The server hears the work areas
 * the reader may read and none other; the frame is the same as one work area's. A work area
 * behind a desktop connection is not on this socket and keeps its own stream.
 */
export function subscribeToDataChangesOf(
  workAreaIds: readonly string[],
  onChange: (change: IWorkAreaDataChange) => void,
  onError?: (error: unknown) => void,
): () => void {
  return realtimeSubscribe<{ workAreasDataChanges: IWorkAreaDataChange }>(
    {
      query: `subscription WorkAreasDataChanges($workAreaIds: [ID!]!) {
      workAreasDataChanges(workAreaIds: $workAreaIds) {
        workAreaId source itemId recordKind recordId revision at
      }
    }`,
      variables: { workAreaIds: [...workAreaIds] },
    },
    {
      next: ({ data, errors }) => {
        if (data?.workAreasDataChanges) {
          onChange(data.workAreasDataChanges);
          return;
        }
        onError?.(
          new Error(
            errors?.length
              ? `Live updates rejected by the server: ${errors.map((entry) => entry.message).join('; ')}`
              : 'Live updates delivered a frame with no change.',
          ),
        );
      },
      error: (error) => onError?.(error),
      complete: () => undefined,
    },
  );
}

export function subscribeToDataChanges(
  workAreaId: string,
  onChange: (change: IWorkAreaDataChange) => void,
  onError?: (error: unknown) => void,
): () => void {
  return realtimeSubscribeFor<{ workAreaDataChanges: IWorkAreaDataChange }>(
    workAreaId,
    {
      query: `subscription WorkAreaDataChanges($workAreaId: ID!) {
      workAreaDataChanges(workAreaId: $workAreaId) {
        workAreaId source itemId recordKind recordId revision at
      }
    }`,
      variables: { workAreaId },
    },
    {
      next: ({ data, errors }) => {
        if (data?.workAreaDataChanges) {
          // The model's item types and parts move through the doorbell whoever wrote them (I-1504).
          if (changesGenericViewMetadata(data.workAreaDataChanges)) {
            forgetGenericViewMetadata(data.workAreaDataChanges.workAreaId);
          }
          onChange(data.workAreaDataChanges);
          return;
        }
        onError?.(
          new Error(
            errors?.length
              ? `Live updates rejected by the server: ${errors.map((entry) => entry.message).join('; ')}`
              : 'Live updates delivered a frame with no change.',
          ),
        );
      },
      error: (error) => {
        onError?.(error);
      },
      complete: () => undefined,
    },
  );
}
